import {
    ChangeDetectionStrategy,
    ViewEncapsulation,
    Component,
    inject,
    ElementRef,
    HostBinding,
    effect,
    computed,
    DestroyRef,
    input,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, AbstractControl, ValidationErrors } from '@angular/forms';
import { IOnboardingEditItem } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { FiOnboardingCategoryType, FiOnboardingDisplayType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { BehaviorSubject } from 'rxjs';
import { FiOnboardingBuilderStoreService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-builder-store.service';
import { AppTextService } from '@fi-shared/app-text';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface IOnboardingActivateSettings {
    DisplayType: FiOnboardingDisplayType;
    IsActive: boolean;
}

@Component({
    selector: 'fi-onboarding-activate-settings',
    templateUrl: './fi-activate-settings.component.html',
    styleUrls: ['./fi-activate-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-activate-settings', '[class.hidden]': 'hidden()' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiOnboardingActivateSettingsComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiOnboardingActivateSettingsComponent,
            multi: true,
        },
    ],
})
export class FiOnboardingActivateSettingsComponent extends ControlValueAccessorValidatorBase<IOnboardingActivateSettings> {
    public readonly scrollableElementRefs = input<ElementRef<HTMLElement>[]>([]);
    public readonly hidden = input<boolean>(false);
    private readonly _hiddenChangeEffect = effect(() => {
        this.onInvalidStatusUpdate();
    });

    protected readonly FiOnboardingDisplayType = FiOnboardingDisplayType;
    protected readonly FiOnboardingCategoryType = FiOnboardingCategoryType;
    protected readonly FiTooltipContentType = FiTooltipContentType;
    private readonly fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private readonly appTextService = inject(AppTextService);
    private readonly destroyRef = inject(DestroyRef);
    protected invalidStatus$ = new BehaviorSubject<boolean>(false);

    private onboarding = computed<IOnboardingEditItem>(() => this.fiOnboardingBuilderStoreService.currentOnboarding());
    private _isShared = computed<boolean>(() => this.onboarding()?.IsShared);
    private _category = computed<FiOnboardingCategoryType>(() => this.onboarding()?.Category);
    protected dispalyTypeSettingsVisible = computed<boolean>(() => {
        // https://chainformation.atlassian.net/browse/FI-6679?focusedCommentId=94676
        return false; // this._category() === FiOnboardingCategoryType.Independent || this._isShared();
    });

    protected canActivate = computed<boolean>(() => {
        return this._category() === FiOnboardingCategoryType.Independent && this._isShared();
    });
    protected activationDisabledReason = computed<string>(() => {
        if (this._category() === FiOnboardingCategoryType.Independent) {
            return this.appTextService.get('onboarding.activation_disabled_tooltip');
        }
        return this.appTextService.get('onboarding.sync_activation_disabled_tooltip');
    });
    private _isFormSubmitted = false;
    private _isControlStatusSubscribed = false;

    private _formSubmitEffect = effect(() => {
        const formSubmittedCount = this.fiOnboardingBuilderStoreService.formSubmitted();
        if (!formSubmittedCount || this._isFormSubmitted) {
            return;
        }
        this._isFormSubmitted = true;
        this.trySubscribeOnControlStatusChange();
        this.onInvalidStatusUpdate();
    });

    private trySubscribeOnControlStatusChange(): void {
        if (!this._isFormSubmitted || !this.controlComponent || this._isControlStatusSubscribed) {
            return;
        }

        this._isControlStatusSubscribed = true;
        this.controlComponent.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.onInvalidStatusUpdate();
        });
    }

    private onInvalidStatusUpdate(): void {
        if (this.hidden()) {
            this.invalidStatus$.next(false);
            return;
        }
        this.setShowErrorTooltip();
    }

    private setShowErrorTooltip(): void {
        if (!this._isFormSubmitted) {
            this.invalidStatus$.next(false);
            return;
        }
        const errors = this.controlComponent?.errors ?? {};
        const hasErrors = Object.keys(errors).length > 0;
        this.invalidStatus$.next(hasErrors);
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        this.trySubscribeOnControlStatusChange();

        return {
            'missing-display-type': !isDefined(this.ngModel.DisplayType),
            'missing-activation-data': !isDefined(this.ngModel.IsActive),
        };
    }
}
